import { z } from 'zod';

// The payments ledger (/api/payments). Schemas check shape only: allowed status transitions, the
// note guard (card numbers, SINs, banking details) and the database lookups stay in the services.
// The values below mirror lib/paymentRules.ts and lib/paymentRoutes.ts (the tests compare them).

export const PAYMENT_MAX_AMOUNT_CENTS = 10_000_000;
const UUID = /^[0-9a-f-]{36}$/i;
const FISCAL_YEAR = /^\d{4}(-\d{2})?$/;
const YORK_REFERENCE = /^[A-Za-z0-9][A-Za-z0-9 ._/#-]*$/;

export const PaymentKind = z.enum(['stipend', 'honorarium', 'gift_card', 'reimbursement', 'registration', 'prize']);
export type PaymentKind = z.infer<typeof PaymentKind>;

export const PaymentStatus = z.enum(['owed', 'requested', 'submitted', 'approved', 'paid', 'cleared', 'cancelled']);
export type PaymentStatus = z.infer<typeof PaymentStatus>;

/** The statuses a new entry may start in. */
export const PaymentInitialStatus = z.enum(['owed', 'requested', 'submitted', 'approved', 'paid']);
export type PaymentInitialStatus = z.infer<typeof PaymentInitialStatus>;

export const PaymentRouteKey = z.enum([
  'member_travel',
  'honorarium',
  'non_resident',
  'participant_fee',
  'invoice_requisition',
  'prepaid_flight',
]);
export type PaymentRouteKey = z.infer<typeof PaymentRouteKey>;

export const PaymentClaimLineType = z.enum([
  'airfare',
  'train',
  'taxi',
  'transit',
  'meal',
  'parking',
  'hotel',
  'mileage',
  'other',
]);
export type PaymentClaimLineType = z.infer<typeof PaymentClaimLineType>;

const Id = z.string().regex(UUID, 'is not an id');
const OptionalId = z.union([Id, z.literal('')]).nullish();

/** Whole cents; null, '' or absent mean no amount. Digit strings are read as numbers by the service. */
const Cents = z
  .union([
    z.number().int().min(0).max(PAYMENT_MAX_AMOUNT_CENTS),
    z
      .string()
      .regex(/^\d+$/, 'must be whole cents')
      .refine((s) => Number(s) <= PAYMENT_MAX_AMOUNT_CENTS, 'must be at most 10,000,000 cents'),
    z.literal(''),
    z.null(),
  ])
  .optional();

/** Free text, trimmed by the service; blank means none. */
const Text = (max: number) =>
  z
    .string()
    .refine((s) => s.trim().length <= max, `must be at most ${max} characters`)
    .nullish();

const YorkReference = z
  .string()
  .refine((s) => s.trim().length <= 64, 'must be at most 64 characters')
  .refine(
    (s) => s.trim() === '' || YORK_REFERENCE.test(s.trim()),
    'may hold letters, digits, spaces and . _ / # - only',
  )
  .nullish();

/** Blank means the current fiscal year (or no filter). */
const FiscalYearText = z.string().refine((s) => s === '' || FISCAL_YEAR.test(s.trim()), 'looks like 2026-27');

const CommaList = (values: readonly string[], label: string) =>
  z.string().refine((s) => s === '' || s.split(',').every((v) => values.includes(v)), `Unknown ${label}`);

const Digits = z.string().regex(/^\d+$/, 'must be a whole number');

// ---------------------------------------------------------------- requests

const filterShape = {
  kind: CommaList(PaymentKind.options, 'kind').optional(),
  status: CommaList(PaymentStatus.options, 'status').optional(),
  fiscalYear: FiscalYearText.optional(),
  participantId: z.union([Id, z.literal('')]).optional(),
  eventId: z.union([Id, z.literal('')]).optional(),
  cycleId: z.union([Id, z.literal('')]).optional(),
  userId: z.union([Id, z.literal('')]).optional(),
  contactId: z.union([Id, z.literal('')]).optional(),
  /** '1' keeps only entries that need chasing; any other value is ignored. */
  stale: z.string().optional(),
};

/** GET /api/payments/summary (viewers: only fiscalYear is used). */
export const PaymentSummaryQuery = z.strictObject(filterShape);
export type PaymentSummaryQuery = z.input<typeof PaymentSummaryQuery>;

/** GET /api/payments; page and limit are clamped by the service (limit 1–500, default 100). */
export const PaymentListQuery = z.strictObject({ ...filterShape, page: Digits.optional(), limit: Digits.optional() });
export type PaymentListQuery = z.input<typeof PaymentListQuery>;

export const PaymentIdParams = z.strictObject({ id: Id });
export type PaymentIdParams = z.input<typeof PaymentIdParams>;

/** The step must belong to the entry's route; the service checks that. */
export const PaymentStepParams = z.strictObject({ id: Id, step: z.string() });
export type PaymentStepParams = z.input<typeof PaymentStepParams>;

/** POST /api/payments. Who is paid: email, userId, contactId or a participant's person. */
export const CreatePaymentRequest = z.strictObject({
  kind: PaymentKind,
  status: z.union([PaymentInitialStatus, z.literal('')]).nullish(),
  amountCents: Cents,
  note: Text(2000),
  yorkReference: YorkReference,
  fiscalYear: FiscalYearText.nullish(),
  email: z.string().nullish(),
  userId: OptionalId,
  contactId: OptionalId,
  participantId: OptionalId,
  eventId: OptionalId,
  cycleId: OptionalId,
});
export type CreatePaymentRequest = z.input<typeof CreatePaymentRequest>;

/** PATCH /api/payments/:id; only the fields sent change. */
export const UpdatePaymentRequest = z.strictObject({
  note: Text(2000),
  yorkReference: YorkReference,
  fiscalYear: FiscalYearText.nullish(),
  amountCents: Cents,
});
export type UpdatePaymentRequest = z.input<typeof UpdatePaymentRequest>;

/** POST /api/payments/status. Ids that are not entries are reported as refused, not rejected. */
export const PaymentStatusRequest = z.strictObject({
  ids: z.array(z.string()).min(1, 'Choose at least one entry').max(500, 'At most 500 entries at a time'),
  status: PaymentStatus,
  note: Text(2000),
  yorkReference: YorkReference,
});
export type PaymentStatusRequest = z.input<typeof PaymentStatusRequest>;

/** PUT /api/payments/:id/route; null clears the route. */
export const PaymentRouteRequest = z.strictObject({
  route: PaymentRouteKey.nullish(),
  lostReceipt: z.boolean().optional(),
});
export type PaymentRouteRequest = z.input<typeof PaymentRouteRequest>;

/** POST /api/payments/:id/steps/:step */
export const PaymentStepRequest = z.strictObject({
  undo: z.boolean().optional(),
  note: Text(500),
});
export type PaymentStepRequest = z.input<typeof PaymentStepRequest>;

/** PUT /api/payments/:id/claim-lines */
export const PaymentClaimLinesRequest = z.strictObject({
  lines: z
    .array(z.strictObject({ type: PaymentClaimLineType, amountCents: Cents, note: Text(200) }))
    .max(40, 'At most 40 claim lines'),
});
export type PaymentClaimLinesRequest = z.input<typeof PaymentClaimLinesRequest>;

// ---------------------------------------------------------------- responses (dates as ISO strings)

export const PaymentPerson = z.object({
  type: z.enum(['member', 'contact', 'detached']),
  id: z.string().nullable(),
  name: z.string().nullable(),
  email: z.string().nullable(),
});
export type PaymentPerson = z.infer<typeof PaymentPerson>;

export const PaymentClaimLine = z.object({
  type: PaymentClaimLineType,
  amountCents: z.number().nullable(),
  note: z.string().nullable(),
});
export type PaymentClaimLine = z.infer<typeof PaymentClaimLine>;

export const PaymentEntry = z.object({
  id: z.string(),
  person: PaymentPerson,
  kind: z.string(),
  participantId: z.string().nullable(),
  eventId: z.string().nullable(),
  cycleId: z.string().nullable(),
  activity: z.object({
    cycle: z.object({ id: z.string(), label: z.string(), program: z.string().nullable() }).nullable(),
    event: z.object({ id: z.string(), title: z.string(), startsAt: z.string().nullable() }).nullable(),
  }),
  amountCents: z.number().nullable(),
  currency: z.string(),
  status: z.string(),
  statusChangedAt: z.string(),
  allowedNext: z.array(z.string()),
  yorkReference: z.string().nullable(),
  note: z.string().nullable(),
  fiscalYear: z.string(),
  route: z.string().nullable(),
  lostReceipt: z.boolean(),
  claimLines: z.array(PaymentClaimLine).nullable(),
  source: z.string(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  detachedAt: z.string().nullable(),
});
export type PaymentEntry = z.infer<typeof PaymentEntry>;

export const PaymentHistoryEntry = z.object({
  from: z.string().nullable(),
  to: z.string(),
  at: z.string(),
  by: z.string().nullable(),
  note: z.string().nullable(),
});
export type PaymentHistoryEntry = z.infer<typeof PaymentHistoryEntry>;

export const PaymentEntryDetail = PaymentEntry.extend({ history: z.array(PaymentHistoryEntry) });
export type PaymentEntryDetail = z.infer<typeof PaymentEntryDetail>;

export const PaymentList = z.object({
  entries: z.array(PaymentEntry),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});
export type PaymentList = z.infer<typeof PaymentList>;

export const PaymentStaleCounts = z.object({
  total: z.number(),
  rules: z.array(z.object({ status: z.string(), days: z.number(), label: z.string(), count: z.number() })),
});
export type PaymentStaleCounts = z.infer<typeof PaymentStaleCounts>;

const Totals = z.object({ count: z.number(), amountCents: z.number() });

/** Viewers get the number of entries per status and nothing else. */
export const PaymentSummary = z.discriminatedUnion('countsOnly', [
  z.object({
    countsOnly: z.literal(true),
    byStatus: z.record(z.string(), z.number()),
    stale: z.object({ total: z.number() }),
    fiscalYears: z.array(z.string()),
  }),
  z.object({
    countsOnly: z.literal(false),
    byStatus: z.record(z.string(), Totals.extend({ withoutAmount: z.number() })),
    byKind: z.record(z.string(), Totals.extend({ byStatus: z.record(z.string(), Totals) })),
    stale: PaymentStaleCounts,
    fiscalYears: z.array(z.string()),
    currentFiscalYear: z.string(),
  }),
]);
export type PaymentSummary = z.infer<typeof PaymentSummary>;

export const PaymentStatusResult = z.object({
  changed: z.array(z.object({ id: z.string(), from: z.string(), to: z.string() })),
  refused: z.array(z.object({ id: z.string(), from: z.string().optional(), reason: z.string() })),
});
export type PaymentStatusResult = z.infer<typeof PaymentStatusResult>;

export const PaymentStep = z.object({
  key: z.string(),
  label: z.string(),
  prerequisite: z.boolean(),
  optional: z.boolean(),
  status: z.string().nullable(),
  done: z.boolean(),
  at: z.string().nullable(),
  note: z.string().nullable(),
});
export type PaymentStep = z.infer<typeof PaymentStep>;

/** An entry's route, York steps and claim lines. */
export const PaymentStepView = z.object({
  route: z.string().nullable(),
  routeLabel: z.string().nullable(),
  lostReceipt: z.boolean(),
  steps: z.array(PaymentStep),
  next: z.string().nullable(),
  stuck: z.object({ step: z.string(), days: z.number() }).nullable(),
  claimLines: z.array(PaymentClaimLine),
});
export type PaymentStepView = z.infer<typeof PaymentStepView>;

export const PaymentStepsResponse = PaymentStepView.extend({ id: z.string(), status: z.string() });
export type PaymentStepsResponse = z.infer<typeof PaymentStepsResponse>;

export const PaymentClaimLinesResponse = PaymentStepsResponse.extend({ amountCents: z.number().nullable() });
export type PaymentClaimLinesResponse = z.infer<typeof PaymentClaimLinesResponse>;

export const PaymentRouteList = z.object({
  routes: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      steps: z.array(
        z.object({
          key: z.string(),
          label: z.string(),
          prerequisite: z.boolean(),
          optional: z.boolean(),
          lostReceiptOnly: z.boolean(),
        }),
      ),
    }),
  ),
  claimLineTypes: z.record(z.string(), z.string()),
  defaultRoutes: z.record(z.string(), z.string()),
  stuckDays: z.number(),
});
export type PaymentRouteList = z.infer<typeof PaymentRouteList>;
